/** @format */

import type React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import styled, { keyframes } from "styled-components";
import { useFontStyles } from "../../../hooks/useFontStyles";
import { cn } from "../../../util/classNameConstructor";

const skeletonKeyframes = keyframes`
  0% {
    background-position: 0% 0;
  }
  100% {
    background-position: -200% 0;
  }
`;

const SkeletonItem = styled.span`
  animation: ${skeletonKeyframes} 2s cubic-bezier(0.75, 0, 0.25, 1) infinite;
  background-size: 200% 100%;
`;

type SkeletonProps = {
  count?: number;
  height?: number;
  width?: number;
  round?: boolean;
  square?: boolean;
  ragged?: boolean;
  className?: string;
  classNames?: {
    item?: string;
  };
};
const Skeleton = ({
  count = 1,
  height,
  width,
  round,
  square,
  ragged,
  className,
  classNames,
}: SkeletonProps) => {
  const [{ lineHeight, fontSize }, containerRef] = useFontStyles();
  const { height: baseHeight, marginBlock } = useMemo(() => {
    return {
      height: height ? `${height}px` : fontSize ? `${fontSize - 2}px` : "1em",
      marginBlock:
        !height && fontSize
          ? `${Math.max(0, lineHeight - (fontSize - 2)) / 2}px`
          : "4px",
    };
  }, [height, fontSize, lineHeight]);

  // Generate natural-looking paragraph widths when ragged is true
  const raggedWidths = ragged
    ? ["91%", "100%", "85%", "97%", "74%", "94%", "100%", "83%", "75%", "45%"]
    : [];

  return (
    <div
      ref={containerRef}
      className={cn("relative w-52 flex flex-col", className)}
    >
      {Array.from({ length: count }).map((_, i) => (
        <SkeletonItem
          key={i.toString()}
          className={cn(
            "w-full rounded-md bg-gradient-to-r from-neutral-200 via-neutral-100 to-neutral-200",
            round && "rounded-full",
            square && "aspect-square w-min",
            classNames?.item,
          )}
          style={{
            animationDelay: `${i * 0.1}s`,
            marginBlock,
            height: baseHeight,
            width: width
              ? `${width}px`
              : ragged
                ? raggedWidths[i % raggedWidths.length]
                : undefined,
          }}
        />
      ))}
    </div>
  );
};

export default Skeleton;
